/**
 * HomePage - Welcome/Landing page redesigned to match WelcomePage.html
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SignInModal from '../components/auth/SignInModal';

export default function HomePage() {
  const { isAuthenticated } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);

  if (isAuthenticated) return <Navigate to="/trips" replace />;

  const handleCTAClick = () => {
    setShowSignIn(true);
  };

  return (
    <div className="bg-background-light min-h-screen flex flex-col justify-center relative overflow-hidden">
      {/* Top gradient background */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-cyan-100/50 to-transparent pointer-events-none"></div>

      {/* Main Content */}
      <main className="w-full max-w-md mx-auto px-6 py-12 flex flex-col items-center justify-center relative z-10 h-full">
        {/* Icon */}
        <div className="mb-8 p-4 bg-white rounded-full shadow-lg ring-4 ring-cyan-50 animate-fade-in-up">
          <svg className="w-16 h-16 md:w-20 md:h-20 text-[#0dccf2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-black text-center mb-4 tracking-tight text-slate-900 drop-shadow-sm">
          旅程規劃
        </h1>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-slate-600 text-center mb-10 leading-relaxed max-w-xs mx-auto font-medium">
          輕鬆規劃您的完美旅程，<br />記錄每一天的精彩行程
        </p>

        {/* CTA Button */}
        <button
          onClick={handleCTAClick}
          className="w-full max-w-xs bg-[#0dccf2] hover:bg-[#0bb6d9] text-white text-xl font-bold py-4 px-8 rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 transform hover:-translate-y-0.5 active:scale-95 mb-10 flex items-center justify-center gap-2 group"
        >
          <span>登入</span>
          <svg
            className="w-5 h-5 group-hover:translate-x-1 transition-transform"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>

        {/* Features List */}
        <div className="flex flex-col gap-3 text-slate-600 text-base font-medium">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-cyan-100 text-[#0dccf2]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span>建立旅程並設定日期</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-cyan-100 text-[#0dccf2]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span>為每一天安排活動</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-cyan-100 text-[#0dccf2]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span>拖曳調整行程順序</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="absolute bottom-6 w-full text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Tripway
      </footer>

      {/* Sign In Modal */}
      <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}
